import React, { useState, useEffect, useRef, memo } from 'react';
import {
  Building2, Star, ChevronRight, Users, Briefcase, ExternalLink,
  X, Sparkles, Shield, Zap, ArrowRight, CheckCircle, TrendingUp
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import PortfolioReviews from './PortfolioReviews';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATS_REFRESH_MS = 60_000; // 60s live polling

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

/* ───── Animated counter — only animates on real value change ───── */
const AnimatedCounter = memo(({ value, duration = 1400 }) => {
  const [display, setDisplay] = useState(value || 0);
  const lastValue = useRef(value);
  const rafId = useRef(null);

  useEffect(() => {
    if (value == null) return;
    if (lastValue.current === value) return; // skip re-render with same value
    const from = lastValue.current ?? 0;
    const to = Number(value) || 0;
    lastValue.current = to;

    let start;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (progress < 1) rafId.current = requestAnimationFrame(step);
    };
    rafId.current = requestAnimationFrame(step);
    return () => rafId.current && cancelAnimationFrame(rafId.current);
  }, [value, duration]);

  return <span>{(display || 0).toLocaleString()}</span>;
});

/* ───── 3D tilt card — uses refs/CSS vars; NO re-renders on hover ───── */
const TiltCard = ({ children, className = '', onClick, dataTestId, accent = '#00E5FF' }) => {
  const wrapRef = useRef(null);
  const shineRef = useRef(null);

  const onMove = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const px = x / rect.width, py = y / rect.height;
    const ry = (px - 0.5) * 10;
    const rx = (0.5 - py) * 10;
    el.style.transform = `perspective(1000px) rotateX(${rx}deg) rotateY(${ry}deg)`;
    if (shineRef.current) {
      shineRef.current.style.background =
        `radial-gradient(400px circle at ${px * 100}% ${py * 100}%, ${accent}26, transparent 40%)`;
      shineRef.current.style.opacity = '1';
    }
  };
  const onLeave = () => {
    const el = wrapRef.current;
    if (el) el.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg)';
    if (shineRef.current) shineRef.current.style.opacity = '0';
  };

  return (
    <div ref={wrapRef} onMouseMove={onMove} onMouseLeave={onLeave} onClick={onClick}
      data-testid={dataTestId}
      style={{ transition: 'transform 0.2s ease-out', willChange: 'transform' }}
      className={`relative group cursor-pointer ${className}`}>
      <div ref={shineRef} aria-hidden
        className="absolute inset-0 rounded-2xl pointer-events-none transition-opacity duration-200"
        style={{ opacity: 0 }} />
      {children}
    </div>
  );
};

/* ───── Live stats row — reserves space so layout doesn't shift ───── */
const LiveStatsRow = memo(({ stats, accent }) => {
  const ready = stats && !stats.error && stats.member_count != null;
  return (
    <div className="flex items-center gap-3 text-[10px] text-white/50 min-h-[16px]" data-testid="live-stats-row">
      {ready ? (
        <>
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" style={{ color: accent }} />
            <AnimatedCounter value={stats.member_count} /> members
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live
          </span>
        </>
      ) : (
        <span className="flex items-center gap-1 text-white/20">
          <Users className="w-3 h-3" /> <span>—</span>
        </span>
      )}
    </div>
  );
});

const PortfolioShowcase = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [liveStats, setLiveStats] = useState({});

  // Fetch groups once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/portfolio/public`);
        if (!cancelled) setGroups(r.data);
      } catch (e) {
        console.error('Failed to load portfolio', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live polling for Roblox stats — only when groups load, every 60s
  useEffect(() => {
    if (!groups.length) return;
    let cancelled = false;
    const fetchStats = async () => {
      const withIds = groups.filter(g => g.roblox_group_id);
      const results = await Promise.allSettled(
        withIds.map(g => axios.get(`${BACKEND_URL}/api/portfolio/roblox-stats/${g.roblox_group_id}`))
      );
      if (cancelled) return;
      // Merge so unaffected entries keep prior values (no flash)
      setLiveStats(prev => {
        const next = { ...prev };
        withIds.forEach((g, i) => {
          if (results[i].status === 'fulfilled') next[g.id] = results[i].value.data;
        });
        return next;
      });
    };
    fetchStats();
    const interval = setInterval(fetchStats, STATS_REFRESH_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [groups]);

  const types = ['All', ...Array.from(new Set(groups.map(g => g.type).filter(Boolean)))];
  const filtered = groups.filter(g => {
    const matchType = filter === 'All' || g.type === filter;
    const matchSearch = !search || g.name.toLowerCase().includes(search.toLowerCase()) ||
      (g.short_description || '').toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  const featured = filtered.find(g => g.featured) || filtered[0];
  const rest = filtered.filter(g => g.id !== featured?.id);
  const totalMembers = Object.values(liveStats).reduce((sum, s) => sum + (s?.member_count || 0), 0);

  const selectedStats = selected ? liveStats[selected.id] : null;

  return (
    <section id="groups" className="py-24 relative" data-testid="portfolio-showcase-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#00E5FF]/[0.02] rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] mb-5">
            <Building2 className="w-3 h-3 text-[#00E5FF]" />
            <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-medium">Our Portfolio</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-light tracking-tight text-white mb-3">
            Premium <span className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent font-medium">Establishments</span>
          </h2>
          <p className="text-white/25 text-sm max-w-md mx-auto">A curated collection of exceptional venues under our management</p>

          {/* Reserve space so it doesn't pop in */}
          <div className="h-9 mt-5 flex items-center justify-center">
            {totalMembers > 0 && (
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/[0.04] backdrop-blur-sm">
                <TrendingUp className="w-3 h-3 text-emerald-400" />
                <span className="text-xs text-white/60">
                  <AnimatedCounter value={totalMembers} /> combined members
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              </div>
            )}
          </div>
        </div>

        {/* Filter tabs + search */}
        {!loading && groups.length > 0 && (
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-10">
            <div className="flex items-center gap-2 flex-wrap justify-center" data-testid="portfolio-filter-tabs">
              {types.map(t => (
                <button key={t} onClick={() => setFilter(t)} data-testid={`filter-tab-${t}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium tracking-wide transition-all border ${filter === t
                    ? 'bg-white text-black border-white'
                    : 'bg-white/[0.03] text-white/50 border-white/10 hover:text-white hover:border-white/25'}`}>
                  {t}
                </button>
              ))}
            </div>
            <div className="relative">
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search groups..." data-testid="portfolio-public-search"
                className="px-4 py-2 bg-white/[0.03] border border-white/10 rounded-full text-white text-xs placeholder-white/25 focus:outline-none focus:border-[#00E5FF]/40 w-56" />
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-20 text-white/30 text-sm">
            <div className="w-8 h-8 border-2 border-[#00E5FF]/30 border-t-[#00E5FF] rounded-full animate-spin mx-auto mb-3" />
            Loading portfolio...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/30 text-sm">No groups match your filters.</div>
        ) : (
          <>
            {/* Featured */}
            {featured && (
              <TiltCard accent={featured.accent_color} dataTestId="featured-group"
                onClick={() => setSelected(featured)}
                className="mb-6 rounded-2xl border border-white/[0.06] bg-white/[0.015] hover:bg-white/[0.025] overflow-hidden transition-colors"
              >
                <div className="grid md:grid-cols-2 gap-0 rounded-2xl overflow-hidden">
                  <div className="relative h-64 md:h-80 overflow-hidden">
                    {featured.image_url && (
                      <img src={getImageUrl(featured.image_url)} alt={featured.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#05050A] hidden md:block" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] to-transparent md:hidden" />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <Badge className="bg-[#00E5FF]/80 text-white border-none text-[10px] backdrop-blur-sm">Featured</Badge>
                      {featured.is_hiring && (
                        <Badge className="bg-emerald-500/90 text-white border-none text-[10px] flex items-center gap-1">
                          <Briefcase className="w-2.5 h-2.5" />Now Hiring
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="p-8 md:p-10 flex flex-col justify-center">
                    <Badge className="w-fit mb-3 bg-white/[0.03] border-white/[0.06] text-white/40 text-[10px]">{featured.type}</Badge>
                    <h3 className="text-2xl md:text-3xl font-light tracking-tight text-white mb-2">
                      {featured.name}
                    </h3>
                    {featured.tagline && <p className="text-white/40 italic text-sm mb-3">"{featured.tagline}"</p>}
                    <p className="text-white/25 text-sm leading-relaxed mb-5">{featured.short_description}</p>
                    <LiveStatsRow stats={liveStats[featured.id]} accent={featured.accent_color} />
                    <span className="mt-5 text-xs font-medium flex items-center gap-1 group-hover:gap-2 transition-all"
                      style={{ color: featured.accent_color }}>
                      Explore <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </TiltCard>
            )}

            {/* Rest */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {rest.map((g) => (
                <TiltCard key={g.id} accent={g.accent_color} dataTestId={`group-card-${g.id}`}
                  onClick={() => setSelected(g)}
                  className="rounded-xl border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.03] overflow-hidden transition-colors"
                >
                  <div className="rounded-xl overflow-hidden">
                    <div className="relative h-40 overflow-hidden">
                      {g.image_url && (
                        <img src={getImageUrl(g.image_url)} alt={g.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-transparent to-transparent" />
                      <div className="absolute top-3 left-3 flex gap-1.5">
                        <Badge className="bg-black/50 text-white/70 border-none text-[9px] backdrop-blur-sm">{g.type}</Badge>
                        {g.is_hiring && (
                          <Badge className="bg-emerald-500/90 text-white border-none text-[9px] flex items-center gap-0.5">
                            <Briefcase className="w-2 h-2" />Hiring
                          </Badge>
                        )}
                      </div>
                      {g.featured && (
                        <Star className="absolute top-3 right-3 w-3.5 h-3.5 text-yellow-400 fill-yellow-400 drop-shadow-md" />
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-medium text-white mb-1.5 tracking-tight">
                        {g.name}
                      </h3>
                      <p className="text-white/20 text-xs line-clamp-2 leading-relaxed mb-3">{g.short_description}</p>
                      <div className="flex items-center justify-between">
                        <LiveStatsRow stats={liveStats[g.id]} accent={g.accent_color} />
                        <ChevronRight className="w-3 h-3 text-white/30" />
                      </div>
                    </div>
                  </div>
                </TiltCard>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-[#0A0A12] border border-white/10 text-white max-w-2xl p-0 overflow-hidden shadow-2xl shadow-black/50 [&>button]:hidden" data-testid="portfolio-modal">
          <DialogTitle className="sr-only">Group Details</DialogTitle>
          {selected && (
            <>
              <div className="relative h-56 md:h-72 overflow-hidden">
                {selected.image_url && (
                  <img src={getImageUrl(selected.image_url)} alt={selected.name} className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A12] via-[#0A0A12]/40 to-transparent" />
                <button onClick={() => setSelected(null)}
                  className="absolute top-4 right-4 w-9 h-9 bg-black/40 backdrop-blur-md rounded-xl flex items-center justify-center text-white/60 hover:text-white border border-white/10 transition-all"
                  data-testid="close-portfolio-modal">
                  <X className="w-4 h-4" />
                </button>
                <div className="absolute top-4 left-4 flex gap-2">
                  <Badge className="text-white border-none text-[10px] backdrop-blur-sm"
                    style={{ background: `${selected.accent_color}cc` }}>{selected.type}</Badge>
                  {selected.is_hiring && (
                    <Badge className="bg-emerald-500/90 text-white border-none text-[10px]">
                      <Briefcase className="w-2.5 h-2.5 mr-1" />Now Hiring
                    </Badge>
                  )}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h2 className="text-2xl md:text-3xl font-light tracking-tight text-white">{selected.name}</h2>
                  {selected.tagline && <p className="text-white/50 text-xs mt-1 italic">"{selected.tagline}"</p>}
                  <p className="text-xs mt-1" style={{ color: selected.accent_color }}>Managed by Starnight Holdings</p>
                </div>
              </div>

              <div className="p-6 space-y-5">
                <div className="grid grid-cols-3 gap-2.5">
                  <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.04] text-center">
                    <Users className="w-4 h-4 mx-auto mb-1" style={{ color: selected.accent_color }} />
                    <p className="text-lg font-light text-white">
                      {selectedStats?.member_count != null
                        ? <AnimatedCounter value={selectedStats.member_count} />
                        : <span className="text-white/30">—</span>}
                    </p>
                    <p className="text-[9px] text-white/30 uppercase tracking-wider">Members</p>
                  </div>
                  <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.04] text-center">
                    <Zap className="w-4 h-4 mx-auto mb-1 text-emerald-400" />
                    <p className="text-lg font-light text-white">Live</p>
                    <p className="text-[9px] text-white/30 uppercase tracking-wider">Status</p>
                  </div>
                  <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.04] text-center">
                    <Shield className="w-4 h-4 mx-auto mb-1 text-amber-400" />
                    <p className="text-lg font-light text-white">Verified</p>
                    <p className="text-[9px] text-white/30 uppercase tracking-wider">Management</p>
                  </div>
                </div>

                {selectedStats?.shout?.body && (
                  <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                    <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" style={{ color: selected.accent_color }} />
                      Latest Shout
                    </p>
                    <p className="text-white/70 text-sm italic">"{selectedStats.shout.body}"</p>
                    {selectedStats.shout.poster && (
                      <p className="text-white/30 text-[10px] mt-1">— {selectedStats.shout.poster}</p>
                    )}
                  </div>
                )}

                <div>
                  <h3 className="text-xs font-medium mb-2 uppercase tracking-wider flex items-center gap-1.5"
                    style={{ color: selected.accent_color }}>
                    <Sparkles className="w-3.5 h-3.5" />About This Establishment
                  </h3>
                  <p className="text-white/30 text-sm leading-relaxed">
                    {selected.full_description || selected.short_description}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  {['Professional Staff', 'Active Community', 'Regular Events', 'Quality Experience'].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/30">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />{f}
                    </div>
                  ))}
                </div>

                {/* ───── Reviews ───── */}
                <div className="pt-4 border-t border-white/[0.05]">
                  <PortfolioReviews groupId={selected.id} accentColor={selected.accent_color} />
                </div>

                {selected.roblox_link && (
                  <Button onClick={() => window.open(selected.roblox_link, '_blank')} data-testid="visit-roblox-btn"
                    className="w-full bg-white text-black hover:bg-white/90 font-medium py-3 rounded-xl text-sm transition-all group">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Visit on Roblox
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PortfolioShowcase;
